<script setup lang="ts">
import { computed } from 'vue';
import { Loader2 } from 'lucide-vue-next';
import { cn } from '../../design-system/utils';
import type { Size, Variant } from '../../design-system/tokens';

interface Props {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  disabled: false,
  fullWidth: false,
  type: 'button',
});

defineEmits<{ click: [event: MouseEvent] }>();

const variantClasses: Record<Variant, string> = {
  primary: 'bg-brand text-text-on-brand hover:bg-brand-hover active:bg-brand-active',
  secondary: 'bg-bg-muted text-text border border-border hover:bg-card hover:border-border-strong',
  ghost: 'bg-transparent text-text hover:bg-bg-muted',
  danger: 'bg-danger text-white hover:opacity-90 active:opacity-80',
  subtle: 'bg-accent/10 text-accent hover:bg-accent/20',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
};

const classes = computed(() =>
  cn(
    'inline-flex items-center justify-center font-medium rounded-md',
    'transition-colors duration-fast ease-standard',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'focus-visible:outline-none focus-visible:shadow-focus',
    variantClasses[props.variant],
    sizeClasses[props.size],
    props.fullWidth && 'w-full',
  ),
);
</script>

<template>
  <button
    :type="type"
    :class="classes"
    :disabled="disabled || loading"
    :aria-busy="loading"
    @click="$emit('click', $event)"
  >
    <Loader2 v-if="loading" class="animate-spin" :size="size === 'lg' ? 18 : 16" aria-hidden="true" />
    <slot />
  </button>
</template>
